import React, { useEffect, useState } from "react";
import { Mountain, Plus, Check } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { useUzkaObrazovka } from "../lib/obrazovka.js";

// Aktivní víkendy klienta (8.7.2026, Vylepšení portálu 4.0).
// 9.7.2026 Masterplan 2.0 update:
//  - katalog víkendů je dynamický z PG (aktivni_vikendy_defs), CSM a owner
//    můžou přidat nový víkend přímo tady (formulář dole)
//  - oprava "Klient nenalezen": backend teď hledá klienta dash-insensitive
//  - onCount hlásí počet zaškrtnutých víkendů do hlavičky ExpandableSection
// Management zaškrtne, kterých víkendů se klientka zúčastnila. Klientka pak
// v portálu (tab Úspěchy, sekce Aktivní víkendy) vidí odemčené odznaky
// + speciální odznak Účastnice aktivního víkendu.
// Data: /api/client-weekends (JSONB clients.data.aktivni_vikendy + katalog).

function notionIdFromUrl(u) {
  const m = String(u || "").match(/([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  return m ? m[1] : "";
}

export default function ClientWeekendsSection({ klientId, klientNotion, readOnly = false, onCount }) {
  const id = klientId || notionIdFromUrl(klientNotion);
  // 9.9.2026 — na telefonu jsou misto zaskrtavatek dlazdice na klepnuti.
  const uzka = useUzkaObrazovka();
  const [weekends, setWeekends] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newNazev, setNewNazev] = useState("");
  const [newMisto, setNewMisto] = useState("");
  const [newEmoji, setNewEmoji] = useState("");
  const [addingDef, setAddingDef] = useState(false);

  const user = getCurrentUser();
  const role = user?.role || "";
  const canEdit = !readOnly && ["owner", "manager", "csm"].includes(role);
  const canAddDef = !readOnly && ["owner", "csm"].includes(role);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    (async () => {
      try {
        const r = await apiFetch(`/api/client-weekends?klient_id=${encodeURIComponent(id)}`);
        const j = await r.json();
        if (j.ok) {
          setWeekends(j.weekends || []);
          setCatalog(j.catalog || []);
          if (onCount) onCount((j.weekends || []).length);
        } else if (j.error) {
          setErr(j.error);
        }
      } catch (_) { /* ticho */ }
      setLoading(false);
    })();
  }, [id]);

  const toggle = async (wid) => {
    if (!canEdit || saving) return;
    const next = weekends.includes(wid) ? weekends.filter((w) => w !== wid) : [...weekends, wid];
    const prev = weekends;
    setWeekends(next); // optimistic
    if (onCount) onCount(next.length);
    setSaving(true);
    setErr("");
    try {
      const r = await apiFetch("/api/client-weekends", { method: "POST", body: JSON.stringify({ klient_id: id, weekends: next }) });
      const j = await r.json();
      if (!j.ok) { setWeekends(prev); if (onCount) onCount(prev.length); setErr(j.error || "Uložení selhalo"); }
    } catch (e) { setWeekends(prev); if (onCount) onCount(prev.length); setErr(e.message || "Uložení selhalo"); }
    setSaving(false);
  };

  const addDef = async () => {
    const nazev = newNazev.trim();
    if (!nazev) return;
    setAddingDef(true);
    setErr("");
    try {
      const r = await apiFetch("/api/client-weekends", {
        method: "POST",
        body: JSON.stringify({ action: "add-def", nazev, misto: newMisto.trim(), emoji: newEmoji.trim() }),
      });
      const j = await r.json();
      if (j.ok) {
        setCatalog(j.catalog || []);
        setNewNazev(""); setNewMisto(""); setNewEmoji("");
        setShowAdd(false);
      } else {
        setErr(j.error || "Přidání selhalo");
      }
    } catch (e) { setErr(e.message || "Přidání selhalo"); }
    setAddingDef(false);
  };

  if (!id || loading) return null;
  if (!canEdit && weekends.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Mountain className="w-4 h-4 text-teal-600" />
        <span className="text-sm font-semibold text-slate-700">Zúčastnila se ({weekends.length})</span>
        {saving ? <span className="text-xs text-slate-400">ukládám…</span> : null}
        {canAddDef && !uzka && (
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg px-2 py-1"
          >
            <Plus className="w-3.5 h-3.5" /> Nový víkend
          </button>
        )}
      </div>
      {err ? <div className="text-sm text-rose-600 mb-2">{err}</div> : null}
      {showAdd && canAddDef && (
        <div className="flex flex-wrap items-center gap-2 mb-3 bg-teal-50/60 border border-teal-200 rounded-xl p-2">
          <input value={newEmoji} onChange={(e) => setNewEmoji(e.target.value)} placeholder="🏕️" maxLength={4}
            className="field w-14 text-center" />
          <input value={newNazev} onChange={(e) => setNewNazev(e.target.value)} placeholder="Např. Jarní víkend 2027"
            className="field flex-1 min-w-[180px]" />
          <input value={newMisto} onChange={(e) => setNewMisto(e.target.value)} placeholder="Místo (např. Beskydy)"
            className="field w-full sm:w-44" />
          <button onClick={addDef} disabled={addingDef || !newNazev.trim()}
            className="btn btn-primary">
            Přidat
          </button>
        </div>
      )}
      {/* 9.9.2026 — telefon: cela dlazdice je klikaci (56 px), misto je pod nazvem,
          takze se u delsiho nazvu nic neurve. Na pocitaci zustava puvodni seznam. */}
      {uzka && (
        <div className="space-y-2">
          {catalog.map((w) => {
            const checked = weekends.includes(w.id);
            return (
              <button
                key={w.id}
                type="button"
                disabled={!canEdit}
                onClick={() => toggle(w.id)}
                className={`flex min-h-[56px] w-full items-center gap-3 rounded-xl px-3 text-left ${checked ? "border-2 border-teal-400 bg-teal-50" : "border border-slate-200 bg-white"}`}
              >
                <span className={`text-xl ${checked ? "" : "opacity-40"}`}>{w.emoji}</span>
                <span className="min-w-0 flex-1">
                  <span className={`block text-[14px] font-bold leading-tight ${checked ? "text-slate-800" : "text-slate-500"}`}>{w.nazev}</span>
                  {w.misto ? <span className={`block text-[12px] ${checked ? "text-slate-500" : "text-slate-400"}`}>{w.misto}</span> : null}
                </span>
                <span className={`flex h-7 w-7 flex-none items-center justify-center rounded-full ${checked ? "bg-teal-600 text-white" : "border-2 border-slate-200"}`}>
                  {checked ? <Check className="h-4 w-4" /> : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {canAddDef && uzka && !showAdd && (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="mt-2 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 text-sm font-bold text-slate-600"
        >
          <Plus className="h-4 w-4" /> Nový víkend
        </button>
      )}
      <div className={`${uzka ? "hidden" : "grid"} grid-cols-1 sm:grid-cols-2 gap-1.5`}>
        {catalog.map((w) => {
          const checked = weekends.includes(w.id);
          return (
            <label
              key={w.id}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${checked ? "bg-teal-50 border-teal-300 text-slate-700" : "bg-slate-50 border-slate-200 text-slate-500"} ${canEdit ? "cursor-pointer hover:border-teal-400" : "cursor-default"}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={!canEdit}
                onChange={() => toggle(w.id)}
                className="accent-teal-600"
              />
              <span>{w.emoji}</span>
              <span className="flex-1">{w.nazev}</span>
              <span className="text-xs text-slate-400">{w.misto}</span>
            </label>
          );
        })}
      </div>
      <div className="text-[11px] text-slate-400 mt-1.5">Zaškrtnuté víkendy se klientce ukážou jako odznaky v portálu (Úspěchy, Aktivní víkendy).</div>
    </div>
  );
}
